import React, { useState, useEffect, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
} from 'chart.js';

import TableView from './TableView';
import calculateNetRecurringBenefit from '@/utils/calculation/calculationRecurringBenefit';
import determineStage from '@/utils/DetermineStage';
import { getCancellationDate, getCancellationMonth, getCancellationQuarter, getCancellationWeek, getCancellationYear, getQualitativeKPI, getStageActualMonth, getStageActualQuarter, getStageActualWeek, getStageActualYear, getStageOriginalPlanMonth, getStageOriginalPlanQuarter, getStageOriginalPlanWeek, getStageOriginalPlanYear, getStagePlanMonth, getStagePlanQuarter, getStagePlanWeek, getStagePlanYear, L0ActualDate, L1ActualDate, L1OriginalPlanDate, L2ActualDate, L2OriginalPlanDate, L2PlanDate, L3ActualDate, L3OriginalPlanDate, L3PlanDate, L4ActualDate, L4OriginalPlanDate, L4PlanDate, L5ActualDate, L5OriginalPlanDate, L5PlanDate, SubmittedForL1PlanDate, SubmittedForL2PlanDate, SubmittedForL3PlanDate, SubmittedForL4PlanDate, SubmittedForL5PlanDate } from '@/utils/Sort/checkDate/getDate';
import { check } from 'prettier';
import checkWorkstreamLeadApprovalStatus from '@/utils/Sort/checkWorkstreamLeadApprovalStatus';
import checkWorkstreamSponsorApprovalStatus from '@/utils/Sort/checkWorkstreamSponsorApprovalStatus';
import { checkTOFinanceApprovalStatus, getComplianceStatusForGrouping, getNetRecurringBenefitCatagory, getNetRecurringBenefitCatagoryL0Plus, getNetRecurringBenefitCatagoryL1Plus, getNetRecurringBenefitCatagoryL2Plus, getNetRecurringBenefitCatagoryL3Plus, getNetRecurringBenefitCatagoryL4Plus, LLatestActualDate } from '@/utils/helpers/helpers';
import checkStrategyAnalystApprovalStatus from '@/utils/Sort/checkStrategyAnalystApprovalStatus';
import InitiativePlanningSummary from '@/utils/initiativePlanningSummary';
import planningSummarySimplified from '@/utils/planningSummarySimplified';
import getImplementationCostCatagory from '@/utils/Sort/getImplementationCostCatagory';
import sortInitiativesByActualImpact from '@/utils/Sort/sortByActualImpact';
import sortInitiativesByActuals from '@/utils/Sort/sortByActual';

ChartJS.register(BarElement, Tooltip, Legend, CategoryScale, LinearScale);

const WaterfallChartView = ({
  initiatives,
  selectedGroup,
  groupedData,
  colorByAttribute,
  initiativeColors,
  xAxis,
  setXAxis,
  yAxis,
  setYAxis,
}) => {
  const [showTable, setShowTable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSubworkstreams, setShowSubworkstreams] = useState(false);
  const [selectedSubworkstream, setSelectedSubworkstream] = useState(null);
  const subworkstreamDropdownRef = useRef(null);

  const xOptions = [
    'stage',
    'Stage (simplified)',
    'initiativeOwnerInfo',
    'workstreamLeadInfo',
    'workstreamSponsorInfo',
    'initiativeOwnerBackupInfo',
    'workstreamSponsorWorkstream',
    'workstreamLeadSubworkstream',
    'weeklyStatus',
  ];
  const yOptions = ['Number of Initiatives', 'Net Recurring Benefit'];

  const prepareChartData = () => {
    if (loading) return { labels: [], datasets: [] };

    let filteredInitiatives = initiatives;
    if (selectedGroup === 'workstreamLeadSubworkstream' && selectedSubworkstream) {
      filteredInitiatives = initiatives.filter(
        (initiative) =>
          initiative.workstreamLeadSubworkstream &&
          initiative.workstreamLeadSubworkstream[0]?.subworkstreamName === selectedSubworkstream
      );
    }

    const groupedByXAxis = filteredInitiatives.reduce((acc, initiative) => {
      let key;
      // Use selectedGroup to determine the grouping logic
      switch (selectedGroup) {
        case 'initiativeOwnerInfo':
        case 'workstreamLeadInfo':
        case 'workstreamSponsorInfo':
        case 'initiativeOwnerBackupInfo':
        case 'AllowOnlyTo':
          const person = initiative[selectedGroup]?.[0];
          key = person ? `${person.firstName} ${person.lastName}` : 'Unknown';
          break;
        case 'workstreamSponsorWorkstream':
          key = initiative[selectedGroup]?.[0]?.workstreamName || 'Unknown';
          break;
        case 'workstreamLeadSubworkstream':
          key = initiative[selectedGroup]?.[0]?.subworkstreamName || 'Unknown';
          break;
        case 'stage':
          key = determineStage(initiative);
          break;
        case 'Stage (simplified)':
          key = initiative.stage;
          break;
        case 'selectedForToMeetingDescusion':
        case 'hasFinancialImpact':
          key = initiative[selectedGroup] ? 'Yes' : 'No';
          break;
        case 'Has qualitative KPI?':
          key = getQualitativeKPI(initiative);
          break;
        case 'initiativeNumber - initiativeName':
          key = `${initiative.initiativeNumber} - ${initiative.initiativeName}`;
          break;
        case 'workstreamLeadPendingApproval':
          key = checkWorkstreamLeadApprovalStatus(initiative);
          break;
        case 'workstreamSponsorPendingApproval':
          key = checkWorkstreamSponsorApprovalStatus(initiative);
          break;
        case 'financeLeadPendingApproval':
          key = checkTOFinanceApprovalStatus(initiative);
          break;
        case 'Strategy analyst pending approval?':
          key = checkStrategyAnalystApprovalStatus(initiative);
          break;
        case 'planningSummary':
          key = InitiativePlanningSummary(initiative).planningStatus;
          break;
        case 'Planning summary (simplified)':
          key = planningSummarySimplified(initiative);
          break;
        case 'netRecurringBenefitsRange':
          key = getNetRecurringBenefitCatagory(initiative);
          break;
        case 'Future implementation costs range':
          key = getImplementationCostCatagory(initiative);
          break;
        case 'l2ForecastDate':
          key = L2PlanDate(initiative);
          break;
        case 'l2ForecastDate_Quarter':
          key = getStagePlanQuarter(initiative, 'L2');
          break;
        case 'l2ForecastDate_Month':
          key = getStagePlanMonth(initiative, 'L2');
          break;
        case 'l2ForecastDate_Week':
          key = getStagePlanWeek(initiative, 'L2')
          break;
        case 'L3 date compliance check (forecast vs planned date)':
          key = getComplianceStatusForGrouping(initiative, 'L3');
          break;
        case 'L4 date compliance check (forecast vs planned date)':
          key = getComplianceStatusForGrouping(initiative, 'L4');
          break;
        case 'Number of Attachment':
          key = initiative.attachmentCount;
          break;
        case 'actualImpactUpdateCheck':
        case 'actualKpiUpdateCheck':
          key = sortInitiativesByActualImpact(initiative, selectedGroup);
          break;
        case '(FY24-25) Actual impact update check':
        case '(FY23-24) Actual impact update check':
          key = sortInitiativesByActuals(initiative, selectedGroup);
          break;
        case 'cancellationDate':
          key = getCancellationDate(initiative);
          break;
        case 'l3PlannedDate':
          key = L3PlanDate(initiative);
          break;
        case 'l4PlannedDate':
          key = L4PlanDate(initiative);
          break;
        // L5 Latest Estimated / Forecast Date
        case 'l5LatestEstimatedDate':
        case 'l5ForecastDate':
          key = L5PlanDate(initiative);
          break;
        case 'l0ActualDate':
          key = L0ActualDate(initiative);
          break;
        // L1 Actual Date
        case 'l1ActualDate':
          key = L1ActualDate(initiative);
          break;
        // L2 Actual Date
        case 'l2ActualDate':
          key = L2ActualDate(initiative);
          break;
        // L3 Actual Date
        case 'l3ActualDate':
          key = L3ActualDate(initiative);
          break;
        // L4 Actual Date
        case 'l4ActualDate':
          key = L4ActualDate(initiative);
          break;
        // L5 Actual Date
        case 'l5ActualDate':
          key = L5ActualDate(initiative);
          break;
        // Latest Actual Date (all L Gates)
        case 'latestActualDateAllLGates':
          key = LLatestActualDate(initiative);
          break;
        // Submitted for L1 Approval
        case 'subForL1ApprovDate':
          key = SubmittedForL1PlanDate(initiative);
          break;
        case 'subForL2ApprovDate':
          key = SubmittedForL2PlanDate(initiative);
          break;
        case 'subForL3ApprovDate':
          key = SubmittedForL3PlanDate(initiative);
          break;
        case 'subForL4ApprovDate':
          key = SubmittedForL4PlanDate(initiative);
          break;
        case 'subForL5ApprovDate':
          key = SubmittedForL5PlanDate(initiative);
          break;
        case 'pipelineWeeklyVarianceL0Plus':
          key = getNetRecurringBenefitCatagoryL0Plus(initiative);
          break;
        case 'pipelineWeeklyVarianceL1Plus':
          key = getNetRecurringBenefitCatagoryL1Plus(initiative);
          break;
        case 'pipelineWeeklyVarianceL2Plus':
          key = getNetRecurringBenefitCatagoryL2Plus(initiative);
          break;
        case 'pipelineWeeklyVarianceL3Plus':
          key = getNetRecurringBenefitCatagoryL3Plus(initiative);
          break;
        case 'pipelineWeeklyVarianceL4Plus':
          key = getNetRecurringBenefitCatagoryL4Plus(initiative);
          break;
        default:
          if (selectedGroup?.startsWith('l0ActualDate')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = L0ActualDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageActualYear(initiative, 'L0');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageActualQuarter(initiative, 'L0');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageActualMonth(initiative, 'L0');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageActualWeek(initiative, 'L0');
            }
          }
          else if (selectedGroup?.startsWith('l5LatestEstimatedDate') || selectedGroup?.startsWith('l5ForecastDate')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = L5PlanDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStagePlanYear(initiative, 'L5');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStagePlanQuarter(initiative, 'L5');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStagePlanMonth(initiative, 'L5');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStagePlanWeek(initiative, 'L5');
            }
          }
          else if (selectedGroup?.startsWith('l4PlannedDate')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = L4PlanDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStagePlanYear(initiative, 'L4');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStagePlanQuarter(initiative, 'L4');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStagePlanMonth(initiative, 'L4');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStagePlanWeek(initiative, 'L4');
            }
          }
          else if (selectedGroup?.startsWith('l3PlannedDate')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = L3PlanDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStagePlanYear(initiative, 'L3');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStagePlanQuarter(initiative, 'L3');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStagePlanMonth(initiative, 'L3');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStagePlanWeek(initiative, 'L3');
            }
          }
          else if (selectedGroup?.startsWith('l2ForecastDate')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = L2PlanDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStagePlanYear(initiative, 'L2');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStagePlanQuarter(initiative, 'L2');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStagePlanMonth(initiative, 'L2');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStagePlanWeek(initiative, 'L2');
            }
          }
          else if (selectedGroup?.startsWith('l1ActualDate')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = L1ActualDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageActualYear(initiative, 'L1');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageActualQuarter(initiative, 'L1');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageActualMonth(initiative, 'L1');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageActualWeek(initiative, 'L1');
            }
          }
          else if (selectedGroup?.startsWith('l2ActualDate')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = L2ActualDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageActualYear(initiative, 'L2');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageActualQuarter(initiative, 'L2');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageActualMonth(initiative, 'L2');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageActualWeek(initiative, 'L2');
            }
          }
          else if (selectedGroup?.startsWith('l3ActualDate')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = L3ActualDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageActualYear(initiative, 'L3');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageActualQuarter(initiative, 'L3');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageActualMonth(initiative, 'L3');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageActualWeek(initiative, 'L3');
            }
          }
          else if (selectedGroup?.startsWith('latestActualDateAllLGates')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = LLatestActualDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageActualYear(initiative, 'AllLGates');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageActualQuarter(initiative, 'AllLGates');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageActualMonth(initiative, 'AllLGates');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageActualWeek(initiative, 'AllLGates');
            }
          }
          else if (selectedGroup?.startsWith('l4ActualDate')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = L4ActualDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageActualYear(initiative, 'L4');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageActualQuarter(initiative, 'L4');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageActualMonth(initiative, 'L4');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageActualWeek(initiative, 'L4');
            }
          }
          else if (selectedGroup?.startsWith('l5ActualDate')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = L5ActualDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageActualYear(initiative, 'L5');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageActualQuarter(initiative, 'L5');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageActualMonth(initiative, 'L5');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageActualWeek(initiative, 'L5');
            }
          }
          else if (selectedGroup?.startsWith('subForL1ApprovDate')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = SubmittedForL1PlanDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageActualYear(initiative, 'Submitted for L1 Approval');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageActualQuarter(initiative, 'Submitted for L1 Approval');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageActualMonth(initiative, 'Submitted for L1 Approval');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageActualWeek(initiative, 'Submitted for L1 Approval');
            }
          }
          else if (selectedGroup?.startsWith('subForL2ApprovDate')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = SubmittedForL2PlanDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageActualYear(initiative, 'Submitted for L2 Approval');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageActualQuarter(initiative, 'Submitted for L2 Approval');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageActualMonth(initiative, 'Submitted for L2 Approval');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageActualWeek(initiative, 'Submitted for L2 Approval');
            }
          }
          else if (selectedGroup?.startsWith('subForL3ApprovDate')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = SubmittedForL3PlanDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageActualYear(initiative, 'Submitted for L3 Approval');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageActualQuarter(initiative, 'Submitted for L3 Approval');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageActualMonth(initiative, 'Submitted for L3 Approval');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageActualWeek(initiative, 'Submitted for L3 Approval');
            }
          }
          else if (selectedGroup?.startsWith('subForL4ApprovDate')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = SubmittedForL4PlanDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageActualYear(initiative, 'Submitted for L4 Approval');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageActualQuarter(initiative, 'Submitted for L4 Approval');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageActualMonth(initiative, 'Submitted for L4 Approval');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageActualWeek(initiative, 'Submitted for L4 Approval');
            }
          }
          else if (selectedGroup?.startsWith('subForL5ApprovDate')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = SubmittedForL1PlanDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageActualYear(initiative, 'Submitted for L5 Approval');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageActualQuarter(initiative, 'Submitted for L5 Approval');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageActualMonth(initiative, 'Submitted for L5 Approval');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageActualWeek(initiative, 'Submitted for L5 Approval');
            }
          }
          else if (selectedGroup?.startsWith('L1 Original Planned Date')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = L1OriginalPlanDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageOriginalPlanYear(initiative, 'L1');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageOriginalPlanQuarter(initiative, 'L1');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageOriginalPlanMonth(initiative, 'L1');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageOriginalPlanWeek(initiative, 'L1');
            }
          }
          else if (selectedGroup?.startsWith('L2 Original Planned Date')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = L2OriginalPlanDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageOriginalPlanYear(initiative, 'L2');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageOriginalPlanQuarter(initiative, 'L2');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageOriginalPlanMonth(initiative, 'L2');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageOriginalPlanWeek(initiative, 'L2');
            }
          }
          else if (selectedGroup?.startsWith('L3 Original Planned Date')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = L3OriginalPlanDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageOriginalPlanYear(initiative, 'L3');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageOriginalPlanQuarter(initiative, 'L3');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageOriginalPlanMonth(initiative, 'L3');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageOriginalPlanWeek(initiative, 'L3');
            }
          }
          else if (selectedGroup?.startsWith('L4 Original Planned Date')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = L4OriginalPlanDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageOriginalPlanYear(initiative, 'L4');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageOriginalPlanQuarter(initiative, 'L4');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageOriginalPlanMonth(initiative, 'L4');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageOriginalPlanWeek(initiative, 'L4');
            }
          }
          else if (selectedGroup?.startsWith('L5 Original Planned Date')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = L5OriginalPlanDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getStageOriginalPlanYear(initiative, 'L5');
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getStageOriginalPlanQuarter(initiative, 'L5');
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getStageOriginalPlanMonth(initiative, 'L5');
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getStageOriginalPlanWeek(initiative, 'L5');
            }
          }
          else if (selectedGroup?.startsWith('cancellationDate')) {
            if (selectedGroup?.endsWith('_Day')) {
              key = getCancellationDate(initiative);
            } else if (selectedGroup?.endsWith('_Year')) {
              key = getCancellationYear(initiative);
            }
            else if (selectedGroup?.endsWith('_Quarter')) {
              key = getCancellationQuarter(initiative);
            }
            else if (selectedGroup?.endsWith('_Month')) {
              key = getCancellationMonth(initiative);
            }
            else if (selectedGroup?.endsWith('_Week')) {
              key = getCancellationWeek(initiative);
            }
          }
          else {
            key = initiative[selectedGroup] || 'Unknown';
          }
          break;
      }
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(initiative);
      return acc;
    }, {});

    const labels = Object.keys(groupedByXAxis);

    labels.sort((a, b) => {
      const stageOrder = {
        'L0': 1, 'L1': 2, 'L2': 3, 'L3': 4, 'L4': 5, 'L5': 6, Total: 7,
      };
      return (stageOrder[a] || Infinity) - (stageOrder[b] || Infinity);
    });

    const uniqueWorkstreams = [
      ...new Set(
        filteredInitiatives
          .filter((i) => i.workstreamSponsorWorkstream && i.workstreamSponsorWorkstream[0])
          .map((i) => i.workstreamSponsorWorkstream[0].workstreamName)
      ),
    ].sort();

    const datasets = [];

    const computeYAxisValue = (initiativesInGroup) => {
      if (yAxis === 'Number of Initiatives') {
        return initiativesInGroup.length;
      } else if (yAxis === 'Net Recurring Benefit') {
        return initiativesInGroup.reduce((sum, initiative) => {
          const benefit = calculateNetRecurringBenefit(initiative); // ✅ pass initiative directly
          return sum + benefit;
        }, 0);
      }
      return 0;
    };
    if (colorByAttribute === 'workstreamSponsorWorkstream' && initiativeColors) {
      uniqueWorkstreams.forEach((workstream) => {
        const dataForWorkstream = labels.map((label) => {
          const initiativesInGroup = groupedByXAxis[label] || [];
          const filtered = initiativesInGroup.filter(
            (i) => i.workstreamSponsorWorkstream?.[0]?.workstreamName === workstream
          );
          return computeYAxisValue(filtered);
        });

        datasets.push({
          label: workstream,
          data: dataForWorkstream,
          backgroundColor: initiativeColors[workstream] || '#CCCCCC',
        });
      });
    } else {
      const data = labels.map((label) => {
        const initiativesInGroup = groupedByXAxis[label] || [];
        return computeYAxisValue(initiativesInGroup);
      });

      datasets.push({
        label: yAxis,
        data,
        backgroundColor: '#1890ff',
      });
    }

    return { labels, datasets };
  };

  const chartData = prepareChartData();

  const options = {
    responsive: true,
    plugins: {
      legend: { display: true, position: 'right' },
      tooltip: { enabled: true },
      datalabels: {
        display: false
      }
    },
    scales: {
      x: {
        title: { display: true, text: xAxis },
        stacked: true,
      },
      y: {
        title: { display: true, text: yAxis },
        beginAtZero: true,
        stacked: true,
      },
    },
    onClick: () => setShowTable(true),
    animation: {
      onComplete: () => { }
    }
  };

  const centerValuePlugin = {
    id: 'centerValue',
    afterDatasetsDraw(chart) {
      const { ctx } = chart;
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        meta.data.forEach((bar, index) => {
          const value = dataset.data[index];
          if (value > 0) {
            const x = bar.x;
            const y = bar.y + (bar.height / 2);
            const bgColor = Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor[index] : dataset.backgroundColor;
            const brightness = getBrightness(bgColor);
            const textColor = brightness > 128 ? '#000' : '#fff';

            ctx.shadowColor = 'rgba(0,0,0,0.3)';
            ctx.shadowBlur = 2;
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;

            ctx.fillStyle = textColor;
            ctx.fillText(value.toLocaleString(), x, y);

            ctx.shadowColor = 'transparent';
          }
        });
      });
    }
  };

  const getBrightness = (color) => {
    if (!color) return 128;
    if (color.startsWith('rgba')) {
      const rgba = color.match(/[\d.]+/g);
      if (rgba && rgba.length >= 3) {
        const r = parseInt(rgba[0]);
        const g = parseInt(rgba[1]);
        const b = parseInt(rgba[2]);
        return (r * 299 + g * 587 + b * 114) / 1000;
      }
    } else if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return (r * 299 + g * 587 + b * 114) / 1000;
    }
    return 128;
  };

  ChartJS.register(centerValuePlugin);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        subworkstreamDropdownRef.current &&
        !subworkstreamDropdownRef.current.contains(event.target)
      ) {
        setShowSubworkstreams(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubworkstreamHover = () => {
    if (xAxis === 'workstreamLeadSubworkstream') {
      setShowSubworkstreams(true);
    }
  };

  const handleSubworkstreamSelect = (sub) => {
    setSelectedSubworkstream(sub);
    setShowSubworkstreams(false);
  };

  const uniqueSubworkstreams = [
    ...new Set(
      initiatives
        .filter((i) => i.workstreamLeadSubworkstream && i.workstreamLeadSubworkstream[0])
        .map((i) => i.workstreamLeadSubworkstream[0].subworkstreamName)
    ),
  ];

  return (
    <div>
      <div style={{ marginBottom: '10px' }}>
        <label style={{ marginRight: '5px' }}>X-Axis: </label>
        <select
          value={xAxis}
          onChange={(e) => {
            setXAxis(e.target.value);
            setSelectedSubworkstream(null);
          }}
          style={{ padding: '5px', backgroundColor: '#f0f0f0', color: '#333', border: '1px solid #ccc' }}
          ref={subworkstreamDropdownRef}
          onMouseOver={handleSubworkstreamHover}
        >
          {xOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <label style={{ margin: '0 10px 0 20px' }}>Y-Axis: </label>
        <select
          value={yAxis}
          onChange={(e) => setYAxis(e.target.value)}
          style={{ padding: '5px', backgroundColor: '#f0f0f0', color: '#333', border: '1px solid #ccc' }}
        >
          {yOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        {showSubworkstreams && xAxis === 'workstreamLeadSubworkstream' && (
          <div style={{ position: 'absolute', backgroundColor: 'white', border: '1px solid #ccc', zIndex: 100 }}>
            {uniqueSubworkstreams.map((sub) => (
              <div key={sub} style={{ padding: '5px', cursor: 'pointer' }} onClick={() => handleSubworkstreamSelect(sub)}>
                {sub}
              </div>
            ))}
          </div>
        )}
      </div>

      {!showTable ? (
        <>
          <h3>Initiatives Chart ({yAxis} by {selectedGroup})</h3>
          <Bar
            data={chartData}
            options={{
              ...options,
              plugins: {
                ...options.plugins,
                tooltip: {
                  callbacks: {
                    label: (context) => {
                      const label = context.dataset.label || '';
                      const value = context.raw;
                      return `${label}: ${value.toLocaleString()}`;
                    }
                  }
                }
              }
            }}
            plugins={[centerValuePlugin]}
          />
        </>
      ) : (
        <TableView initiatives={initiatives} />
      )}
    </div>
  );
};

export default WaterfallChartView;